import React from "react";
import { motion } from "framer-motion";
import { Expand, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { IMAGES } from "@/lib/site";

const photos = [
  {
    src: IMAGES.villaOutside,
    title: "Your private villa",
    caption: "A standalone cottage of your own, set in quiet green grounds.",
  },
  {
    src: IMAGES.villaBedroom,
    title: "The bedroom",
    caption: "A calm, ensuite bedroom with AC, made for unbroken sleep.",
  },
  {
    src: IMAGES.villaHall,
    title: "The living hall",
    caption: "A warm sitting room for your bystander and quiet visits.",
  },
  {
    src: IMAGES.villaKitchen,
    title: "A small kitchen",
    caption: "For warm water, herbal tea and small comforts at any hour.",
  },
];

export const VillaGallery = () => {
  const [active, setActive] = React.useState(null);

  return (
    <section
      id="villa"
      className="py-16 md:py-36 bg-sand/40 bg-grain"
      data-testid="villa-gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
            The villas
          </div>
          <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
            Three quiet cottages, <span className="italic text-sage-700">held for one family at a time</span>.
          </h2>
          <p className="mt-5 text-base md:text-lg text-charcoal/65 leading-relaxed">
            A standalone villa for every mother, set in green grounds.
            Modest in detail, generous in light, and entirely yours for the
            length of your stay.
          </p>
        </motion.div>

        {/* Mobile: horizontal swipeable carousel */}
        <div
          className="md:hidden mt-12 -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2"
          style={{ scrollbarWidth: "none" }}
          data-testid="villa-mobile-scroll"
        >
          {photos.map((p, i) => (
            <PhotoTile
              key={`m-${i}`}
              photo={p}
              onOpen={() => setActive(p)}
              className="shrink-0 w-[82%] h-72 snap-start"
              testid={`villa-photo-m-${i + 1}`}
            />
          ))}
          <div className="shrink-0 w-2" aria-hidden />
        </div>
        <div className="md:hidden mt-3 text-center text-xs text-charcoal/45 italic">
          Swipe through the villa →
        </div>

        {/* Desktop: bento grid */}
        <div className="hidden md:grid mt-14 grid-cols-12 gap-6 auto-rows-[260px] lg:auto-rows-[300px]">
          {/* Outside: large left feature */}
          <PhotoTile
            photo={photos[0]}
            onOpen={() => setActive(photos[0])}
            className="col-span-7 row-span-2"
            testid="villa-photo-outside"
          />
          {/* Bedroom: top right */}
          <PhotoTile
            photo={photos[1]}
            onOpen={() => setActive(photos[1])}
            className="col-span-5"
            testid="villa-photo-bedroom"
          />
          {/* Hall: bottom right left half */}
          <PhotoTile
            photo={photos[2]}
            onOpen={() => setActive(photos[2])}
            className="col-span-3"
            testid="villa-photo-hall"
          />
          {/* Kitchen: bottom right right half */}
          <PhotoTile
            photo={photos[3]}
            onOpen={() => setActive(photos[3])}
            className="col-span-2"
            testid="villa-photo-kitchen"
          />
        </div>

        <p className="hidden md:block mt-6 text-xs text-charcoal/50 italic">
          Tap any image to view in full.
        </p>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-4xl bg-ivory border-sage/20 p-0 overflow-hidden rounded-3xl"
          data-testid="villa-lightbox"
        >
          <DialogTitle className="sr-only">{active?.title || "Villa photo"}</DialogTitle>
          {active && (
            <div>
              <img
                src={active.src}
                alt={`${active.title} at Kakkanat Ayurvedic postnatal care villas, Kerala`}
                className="w-full h-auto max-h-[75vh] object-cover"
              />
              <div className="px-6 md:px-8 py-5 border-t border-sage/15">
                <div className="font-serif text-xl text-charcoal">{active.title}</div>
                <p className="text-sm text-charcoal/65 mt-1">{active.caption}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const PhotoTile = ({ photo, onOpen, className = "", testid }) => (
  <motion.button
    type="button"
    onClick={onOpen}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden rounded-3xl border border-sage/15 bg-white text-left ${className}`}
    data-testid={testid}
  >
    <img
      src={photo.src}
      alt={`${photo.title} at Kakkanat Ayurvedic postnatal care villas, Kerala`}
      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] md:group-hover:scale-[1.06]"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/0 to-transparent opacity-90" />
    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 flex items-end justify-between gap-3">
      <div className="text-ivory">
        <div className="font-serif text-lg md:text-xl leading-tight">{photo.title}</div>
        <div className="text-xs md:text-sm text-ivory/75 mt-0.5 line-clamp-1">
          {photo.caption}
        </div>
      </div>
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-ivory/15 backdrop-blur-sm text-ivory shrink-0 transition-colors md:group-hover:bg-sage md:group-hover:text-ivory">
        <Expand size={15} strokeWidth={1.75} />
      </span>
    </div>
  </motion.button>
);

export default VillaGallery;
